/**
 * useDebouncedSave Hook
 *
 * Provides debounced auto-save functionality for form inputs.
 * Prevents form resets during user input by tracking edit state.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { IMerchant } from '@order-up/models';
import { useSaveDraft } from './useApplication';

export interface UseDebouncedSaveOptions {
  applicationId: string;
  currentStep: number;
  debounceMs?: number;
}

export function useDebouncedSave({ applicationId, currentStep, debounceMs = 800 }: UseDebouncedSaveOptions) {
  const { mutate: saveDraft, isPending } = useSaveDraft();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUserEditingRef = useRef(false);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Debounced save function
  const debouncedSave = useCallback((updates: Partial<IMerchant>) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      saveDraft({
        id: applicationId,
        data: updates,
        step: currentStep
      });
      isUserEditingRef.current = false;
    }, debounceMs);
  }, [applicationId, currentStep, saveDraft, debounceMs]);

  // Mark that user is actively editing
  const startEditing = useCallback(() => {
    isUserEditingRef.current = true;
  }, []);

  // Check if user is currently editing
  const isEditing = useCallback(() => {
    return isUserEditingRef.current;
  }, []);

  return {
    debouncedSave,
    startEditing,
    isEditing,
    isSaving: isPending
  };
}
